import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * One line of the catalog's filter block (handoff v2 §6.2, filters spec §7): the axis' label
 * with its count, then its chips. From 1024px the label takes a 180px column — the longest one,
 * "Estado no catálogo (4)", fits it on one line — and the row centres; below, the label sits
 * above the chips and the row starts at the top. A fine rule closes every row.
 */
export function CatalogFilterRow({
  label,
  count,
  labelId,
  children,
  className,
}: {
  label: string;
  /** "(N)", the axis' own count of options, in monospace beside the label. */
  count: string;
  labelId?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "grid grid-cols-[minmax(0,1fr)] items-start gap-x-7 gap-y-2.5 border-b border-[var(--tb-hairline)] py-[13px] [@media(min-width:1024px)]:grid-cols-[180px_minmax(0,1fr)] [@media(min-width:1024px)]:items-center [@media(min-width:1024px)]:py-3.5",
        className,
      )}
    >
      <span
        id={labelId}
        className="inline-flex items-baseline gap-[5px] break-words text-[10.5px] uppercase leading-[1.4] tracking-[0.16em] text-[var(--fg-subtle)]"
      >
        <span>{label}</span>
        <span className="font-mono text-[10.5px] tracking-[0.02em]">{count}</span>
      </span>
      {children}
    </div>
  );
}

/**
 * A track of chips that never wraps: it scrolls sideways, with no scrollbar, at every width
 * (`data-slider` in the prototype). The 1px block padding keeps the chips' borders unclipped.
 */
export const CatalogChipTrack = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex min-w-0 flex-nowrap items-center gap-2 overflow-x-auto overflow-y-hidden py-px [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
      className,
    )}
    {...props}
  />
));
CatalogChipTrack.displayName = "CatalogChipTrack";

/**
 * Brings the pressed chip to the middle of its track when the track is wider than its window
 * (the prototype's `setMacro`): measured before the choice re-renders, applied after it.
 */
export function centreChipInTrack(chip: HTMLElement): void {
  const track = chip.parentElement;
  if (track === null || track.scrollWidth <= track.clientWidth + 4) return;

  const target = Math.max(0, chip.offsetLeft - track.offsetLeft - (track.clientWidth - chip.offsetWidth) / 2);
  requestAnimationFrame(() => {
    track.scrollLeft = target;
  });
}
